import React, {useState} from 'react';
import {
  Button,
  Flex,
  FormControl,
  Input,
  Spinner,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import {ArrowRight} from '@carbon/icons-react';
import {useGenerateAnalystAgent} from '../../../../hooks/api/agents';
import {useTask} from '../../../../hooks/api/tasks';
import {isGenerateAgentState} from '../../../../shared/tasks/state';
import {useQueryClient} from '@tanstack/react-query';
import {MetadataQueryKey} from '../../../../context/MetadataContext';
import {ObjectId} from 'bson';
import {WizardContent} from './WizardContent';

// TODO: add template questions – Hiding the template questions for now because there is a known issue where the user gets stuck in the question input step. It happens when the user submits the exact same question twice, the task then is executed instantly and the client doesn't get the task completion event.
const templateQuestions: string[] = [
  // 'Does this company operate warehouses?',
  // 'How much inbound customer support volume does this company receive?',
  // 'What type of pricing models does this company have?',
];

export const AnalystWizard = ({
  onContinue,
}: {
  onContinue: (agentId: ObjectId) => void;
}) => {
  const [question, setQuestion] = useState('');
  const [agentId, setAgentId] = useState<ObjectId | undefined>();
  const generateAnalystAgent = useGenerateAnalystAgent();
  const {isTaskInProgress} = useTask(
    {type: 'generateAnalystAgent'},
    {
      enabled: !agentId,
      onTaskComplete: (task) => {
        const state = task.detailedState;
        if (!isGenerateAgentState(state)) {
          return;
        }

        setAgentId(state.agentId);

        // re-fetch customer so we have access to new agent
        queryClient.invalidateQueries({
          queryKey: [MetadataQueryKey],
        });
      },
    }
  );
  const queryClient = useQueryClient();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim()) {
      return;
    }

    generateAnalystAgent.mutate({userQuestion: question.trim()});
  };

  if (isTaskInProgress || generateAnalystAgent.isPending) {
    return (
      <WizardContent
        title="Great question. Your agent is planning..."
        footer="This usually takes ~20-35 seconds"
      >
        <Spinner size="xl" color="kred.300" />
      </WizardContent>
    );
  }

  if (agentId) {
    return (
      <WizardContent
        title="Your agent is ready!"
        footer="Now you can adjust the details and test the results"
      >
        <Button
          colorScheme="kbuttonblue"
          size="lg"
          onClick={() => onContinue(agentId)}
          rightIcon={<ArrowRight />}
        >
          Let&apos;s Go
        </Button>
      </WizardContent>
    );
  }

  return (
    <WizardContent title="What do you want to answer about your accounts?">
      <Flex direction="column" gap="8" w="100%" align="center">
        <FormControl
          as="form"
          onSubmit={handleSubmit}
          maxWidth="1000px"
          w="100%"
        >
          <InputGroup>
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask Keyplay anything"
              p="7"
              pr="14"
              fontSize="18px"
              border="2px solid"
              borderColor="kgray.200"
              borderRadius="full"
              bg="kgray.50"
              _hover={{
                borderColor: 'kblue.100',
                boxShadow: 'none',
                outline: 'none',
              }}
              _focus={{
                borderColor: 'kblue.200',
                boxShadow: 'none',
                outline: 'none',
              }}
              transition="all 0.2s ease"
            />
            <InputRightElement height="100%" pr="6">
              <IconButton
                aria-label="Submit question"
                color="kgray.300"
                icon={<ArrowRight size="24px" />}
                variant="ghost"
                isRound
                type="submit"
                isDisabled={!question.trim()}
              />
            </InputRightElement>
          </InputGroup>
        </FormControl>
        <Flex
          gap="2"
          align="center"
          flexWrap="wrap"
          justify="center"
          maxWidth="1000px"
        >
          {templateQuestions.map((template, index) => (
            <Button
              key={index}
              variant="outline"
              size="md"
              onClick={() => setQuestion(template)}
              borderRadius="full"
              px="4"
              py="2"
              whiteSpace="nowrap"
            >
              {template}
            </Button>
          ))}
        </Flex>
      </Flex>
    </WizardContent>
  );
};
